/**
 * Building an absolute URL to hand to someone else.
 *
 * #1118 — the public facilitator profile hardcoded the bare `wellmate.me`
 * apex, plus the profile path, as its share URL. That host redirects to the
 * **old marketing site**, so every channel on that page —
 * clipboard, Messenger, WhatsApp, LinkedIn and the displayed value — handed
 * out a link that did not open the profile it claimed to share. A facilitator
 * who posted it to social could not take it back.
 *
 * The convention here is the one `components/ui/share-qr.tsx` arrived at and
 * documents: **resolve the origin at render time from `window.location.origin`**,
 * with `NEXT_PUBLIC_WEB_URL` only as a fallback. The reason is not style. That
 * env var was never wired into the CI build args, so a build-time origin
 * silently yields nothing, which is how share QRs previously encoded a bare
 * `/events/…` path with no host. Origin-at-render is the only convention that
 * is correct on dev, QA and production **with zero per-environment config**
 * (#1118 NFR-1) — the missing build arg is precisely what caused this class of
 * bug before, so a fix that needs one would be repeating it.
 *
 * The functions here are pure so the rule can be unit-tested in the node
 * environment the web workspace's Vitest runs in, without jsdom: the component
 * supplies the origin, this decides what the URL is.
 */

import { isSafeHttpUrl } from './safe-url'

/** The public profile path for a facilitator. The one place it is spelled. */
export function facilitatorProfilePath(facilitatorId: string): string {
  return `/facilitator-profile/${facilitatorId}`
}

/**
 * Join a resolved origin and an app-relative path into an absolute URL.
 *
 * Returns `null` rather than a partial URL when there is no usable origin.
 * That is deliberate and is #1118 FR-3: pre-hydration (or on a misconfigured
 * build) the share control must not offer a domain-less or wrong-domain value.
 * `ShareQR` makes the same choice for the same reason — it reserves the space
 * rather than render something scannable that is wrong. A caller that treats
 * `null` as "render nothing yet" inherits that guarantee; one that coerces it
 * to a string re-opens the bug.
 *
 * @param base `window.location.origin`, or `NEXT_PUBLIC_WEB_URL`, or nothing.
 * @param path an app-relative path beginning with `/`.
 */
export function resolveShareUrl(base: string | null | undefined, path: string): string | null {
  if (!base) return null

  // Must be an absolute http(s) origin. A relative or malformed value is worse
  // than no value: it produces a link that looks real and opens nothing.
  //
  // `isSafeHttpUrl` (#792) is already the codebase's answer to "is this a URL
  // we are willing to put in front of someone", so the rule lives in one place.
  // Note we deliberately do NOT use its sibling `toSafeHttpHref`, which
  // prefixes a scheme-less value with `https://`: that is right for a
  // facilitator's hand-typed social link, and wrong here. This input is either
  // `window.location.origin` (always absolute) or a `NEXT_PUBLIC_WEB_URL` the
  // env schema already validates as a URL, so a scheme-less value means the
  // environment is misconfigured and should surface as "no link", not be
  // quietly repaired into a guess.
  if (!isSafeHttpUrl(base)) return null
  const origin = new URL(base).origin

  // `URL.origin` never carries a trailing slash, but `NEXT_PUBLIC_WEB_URL` is
  // hand-configured per environment and may; normalising both sides means the
  // result can never contain `//` in the path.
  return `${origin.replace(/\/+$/, '')}/${path.replace(/^\/+/, '')}`
}
